/**
 * Wavlynq billing data — LOCKED from V2 spec §20 (v3.1 LIVE).
 *
 * IDs, names, and display prices are non-negotiable.
 *
 * Stripe is the source of truth for actual price IDs (stored in the
 * `tier_prices` and `alacarte_prices` tables, seeded by migration
 * 0026_v31_live_stripe_price_ids.sql). The frontend only uses the
 * display prices below to render the marketing grid — checkout always
 * resolves the live price by (tier_id, interval) on the server.
 *
 * LIVE Stripe product IDs (for reference / debugging only):
 *   tier_pulse    → prod_UONpUuXNb6NvqH
 *   tier_surge    → prod_UONpVaayEDVQ8X
 *   tier_spectrum → prod_UONpIPF8ssX2Td
 *   tier_attuned  → prod_UONpAkpo441I0W
 *   community_addon_single → prod_UPeLGfeWNKKHSG
 *   community_addon_bundle → prod_UPeLucsw5q4juw
 */

import type { TierId } from "@/lib/profile/types";

export interface Tier {
  id: TierId;
  name: string;
  /** Marketing tagline — short, evocative, under 60 chars. */
  tagline: string;
  /** Monthly USD. Signal is 0. */
  monthlyUsd: number;
  /** Annual USD. Signal is null (free). */
  annualUsd: number | null;
  /** Trial days. Signal = 0, all paid tiers = 7. */
  trialDays: number;
  /** Communities included. 1 for everything except Attuned. */
  communities: 1 | 3;
  /** Highlighted on /pricing as "Most popular". */
  featured?: boolean;
  /** Top selling-points shown on the pricing card. */
  highlights: string[];
}

export const TIERS: Tier[] = [
  {
    id: "tier_signal",
    name: "Signal",
    tagline: "Discover the spectrum.",
    monthlyUsd: 0,
    annualUsd: null,
    trialDays: 0,
    communities: 1,
    highlights: [
      "1 community",
      "25 swipes / day",
      "12 messages / month",
      "Read-only community chat",
      "All safety features included",
    ],
  },
  {
    id: "tier_pulse",
    name: "Pulse",
    tagline: "Send a signal back.",
    monthlyUsd: 9.99,
    annualUsd: 99,
    trialDays: 7,
    communities: 1,
    highlights: [
      "1,000 swipes / day",
      "500 messages / month",
      "Full community chat",
      "Advanced filters",
      "10 voice notes / month",
    ],
  },
  {
    id: "tier_surge",
    name: "Surge",
    tagline: "Build the connection.",
    monthlyUsd: 19.99,
    annualUsd: 199,
    trialDays: 7,
    communities: 1,
    featured: true,
    highlights: [
      "2,000 swipes / day",
      "1,000 messages / month",
      "See who liked you",
      "2 Super Signals / month",
      "3 Boosts / month",
      "Unlimited voice notes",
    ],
  },
  {
    id: "tier_spectrum",
    name: "Spectrum",
    tagline: "The full experience.",
    monthlyUsd: 29.99,
    annualUsd: 299,
    trialDays: 7,
    communities: 1,
    highlights: [
      "Unlimited swipes & messages",
      "AI matchmaker",
      "60-second video intros",
      "7 Boosts / month",
      "1 Profile Spotlight per cycle",
      "Subscription pause",
      "Gift a Premium",
    ],
  },
  {
    id: "tier_attuned",
    name: "Attuned",
    tagline: "All 9 communities. One you.",
    monthlyUsd: 99.99,
    annualUsd: 999,
    trialDays: 7,
    communities: 3, // legacy field — actual access is all 9 sibling apps
    highlights: [
      "Everything in Spectrum",
      "Access to all 9 Wavlynq communities",
      "15 Boosts / month",
      "VIP event priority",
      "Cross-community discovery",
      "RAELA included",
    ],
  },
];

export const ANNUAL_DISCOUNT_LABEL = "Save 17% — 2 months free";

/**
 * Community add-on SKUs (v3.1) — let Pulse / Surge / Spectrum members
 * add extra communities without upgrading to Attuned. Hidden for
 * Attuned (already includes all 9) and Signal (must upgrade first).
 */
export interface CommunityAddOn {
  key: "community_addon_single" | "community_addon_bundle";
  name: string;
  monthlyUsd: number;
  description: string;
}

export const COMMUNITY_ADDONS: CommunityAddOn[] = [
  {
    key: "community_addon_single",
    name: "Community Add-On",
    monthlyUsd: 9.99,
    description: "Add one extra community to your plan.",
  },
  {
    key: "community_addon_bundle",
    name: "3-Community Bundle",
    monthlyUsd: 24.99,
    description: "Add three extra communities and save vs. buying individually.",
  },
];

/** Lookup helper. Returns Signal as fallback. */
export const getTier = (id: TierId): Tier =>
  TIERS.find((t) => t.id === id) ?? TIERS[0];

/** Format USD with two decimals only when needed. */
export const fmtUsd = (n: number): string =>
  n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
